import React from "react";
import Media from "@qlik/qlik/src/components/Media";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * FullWidthMediaCards component.
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */

const FullWidthMediaCards = ({ data }) => {
    // Destructure the data object
    const { title, cardItems = [], additionalConfiguration, customId } = data ?? {};
    // Get the padding class name based on the additional configuration
    const spacingClassNames = getSpacingClassName(additionalConfiguration, "");
    const { sectionColorMapping } = getColorClassName(data?.backgroundColor);
    // Check if the first card item has body content
    const hasBodyContent = cardItems?.[0]?.body;
    const firstMediaItem = cardItems?.[0]?.media?.[0];
    return (
        <section className={`${spacingClassNames} ${sectionColorMapping?.bgColor}`} id={customId}>
            {title && (
                <div className="container">
                    <h2 className={`text-center mb-15 md:mb-0 ${sectionColorMapping.primaryTextColor}`}>{title}</h2>
                </div>
            )}
            <div className={styles.cardsWrapper}>
                <div className={`relative ${hasBodyContent ? "bodyContent" : ""} `}>
                    <div>
                        {firstMediaItem && (
                            <>
                                <Media
                                    media={firstMediaItem}
                                    className="w-full object-cover h-[560px] md:h-[740px]"
                                    videoClassName="rounded"
                                />

                                {firstMediaItem?.mediaCaptionText && (
                                    <span className="play-icon-text">
                                        {firstMediaItem.mediaCaptionText?.mediaCaptionText}
                                    </span>
                                )}
                            </>
                        )}
                    </div>

                    <div className="absolute top-21 xl:top-[168px] w-full">
                        <div className="container">
                            {cardItems?.[0]?.title && (
                                <h2
                                    className={`mb-5 title-2 text-white text-center max-w-[760px] m-auto line-clamp-4 md:line-clamp-2 ${sectionColorMapping.primaryTextColor}`}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(cardItems[0].title) }}
                                />
                            )}
                            {cardItems?.[0]?.body && (
                                <div
                                    className={` max-w-[900px] m-auto ${styles.richTextWrapper} ${sectionColorMapping.primaryTextColor}`}
                                >
                                    {renderRichText(cardItems[0].body, formatContentfulRichText())}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {cardItems?.slice(1) && (
                    <div className="container relative z-1 -mt-15 xl:-mt-30">
                        <div className="pb-15 grid md:grid-cols-4 gap-y-5 md:gap-x-7.5">
                            {cardItems?.slice(1).map((card) => (
                                <div
                                    key={card?.id}
                                    className="shadow-lgLight p-3.75 md:p-5 rounded bg-white flex justify-between md:justify-start gap-x-5 md:flex-col"
                                >
                                    <div className="min-w-12">
                                        {(card?.mediaIcon?.desktopMedia?.length ||
                                            card?.mediaIcon?.mobileMedia?.length) > 0 && (
                                            <Media
                                                media={card.mediaIcon}
                                                className="object-cover w-12 h-12 md:w-17 md:h-17 md:mb-7.5"
                                            />
                                        )}
                                    </div>
                                    <div className={styles.cardBody}>
                                        {card?.title && (
                                            <h3 className="mb-4 md:mb-3.75 text-base tracking-normal md:title-5">
                                                {card.title}
                                            </h3>
                                        )}
                                        {card?.body && (
                                            <div>{renderRichText(card.body, formatContentfulRichText())}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default FullWidthMediaCards;
